import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stagger = (i: number) =>
    loaded ? `${i * 160}ms` : '0ms';

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image — luxury clinic */}
      <div
        className="absolute inset-0 z-0"
        style={{
          transform: `translate(${mousePos.x * -8}px, ${mousePos.y * -8}px) scale(1.08)`,
          transition: 'transform 2s ease-out',
        }}
      >
        <img
          src="https://storage.readdy-site.link/project_files/a5718860-87a7-40cd-af11-814f205df039/698c49a3-e34b-49fe-8f19-1d21d3e5362e_compressed_Captura-de-tela-2026-07-24-191241.webp"
          alt="Infinity Clínica Avançada"
          className="w-full h-full object-cover object-top md:object-[center_15%]"
        />
      </div>

      {/* Dark overlay with warm tone */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background: 'linear-gradient(180deg, rgba(14, 20, 18, 0.55) 0%, rgba(14, 20, 18, 0.70) 40%, rgba(14, 20, 18, 0.75) 60%, rgba(14, 20, 18, 0.80) 100%)',
        }}
      />

      {/* Subtle grain */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
          opacity: 0.04,
        }}
      />

      {/* Ambient glow behind text */}
      <div
        className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none z-[2]"
        style={{
          background: 'radial-gradient(ellipse, rgba(200, 169, 106, 0.06) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      {/* Centered content */}
      <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 max-w-[800px]">
        {/* Main headline */}
        <h1
          className="text-[40px] sm:text-[52px] md:text-[72px] lg:text-[88px] xl:text-[100px] leading-[1.0] mb-6"
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'rgba(248, 247, 245, 0.92)',
            fontWeight: 300,
            letterSpacing: '-0.01em',
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(40px)',
            transition: `opacity 1.2s ease-out ${stagger(1)}, transform 1.2s ease-out ${stagger(1)}`,
          }}
        >
          Dra. Melissa Ciríaco
        </h1>

        {/* Specialties tags */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 md:gap-3 md:gap-x-4 mb-10"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transition: `opacity 0.9s ease-out ${stagger(3)}, transform 0.9s ease-out ${stagger(3)}`,
          }}
        >
          {['EMAGRECIMENTO', 'HORMÔNIOS', 'LIPEDEMA', 'SAÚDE METABÓLICA'].map((tag, i) => (
            <span key={i}>
              <span
                className="text-[10px] md:text-[11px] tracking-[0.2em]"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'rgba(248, 247, 245, 0.35)',
                  fontWeight: 400,
                }}
              >
                {tag}
              </span>
              {i < 3 && (
                <span
                  className="ml-3 md:ml-4 text-[10px]"
                  style={{ color: 'rgba(200, 169, 106, 0.30)' }}
                >
                  ·
                </span>
              )}
            </span>
          ))}
        </div>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center gap-4 mb-10"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transition: `opacity 0.8s ease-out ${stagger(4)}, transform 0.8s ease-out ${stagger(4)}`,
          }}
        >
          <a
            href="https://wa.me/5585999372109?utm_source=site&utm_medium=hero&utm_content=agendar_consulta"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 px-8 md:px-10 py-4 md:py-4.5 rounded-full text-[11px] md:text-xs font-semibold tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-all duration-500"
            style={{
              fontFamily: 'var(--font-body)',
              backgroundColor: '#42554A',
              color: '#F8F7F5',
              boxShadow: '0 4px 24px rgba(66, 85, 74, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4E6457';
              e.currentTarget.style.boxShadow = '0 6px 32px rgba(66, 85, 74, 0.35)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#42554A';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(66, 85, 74, 0.25)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <i className="ri-whatsapp-line text-sm" />
            Agende sua consulta
          </a>
          <a
            href="#raio-x"
            className="group inline-flex items-center gap-2 px-8 md:px-10 py-4 md:py-4.5 rounded-full text-[11px] md:text-xs font-semibold tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-all duration-500"
            style={{
              fontFamily: 'var(--font-body)',
              backgroundColor: '#C8A96A',
              color: '#0E1412',
              boxShadow: '0 4px 24px rgba(200, 169, 106, 0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D4B87A';
              e.currentTarget.style.boxShadow = '0 6px 32px rgba(200, 169, 106, 0.35)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#C8A96A';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(200, 169, 106, 0.25)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Fazer meu check metabólico
            <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>

        {/* Bottom credibility row */}
        <div
          className="flex items-center gap-3 md:gap-4 mt-12 flex-wrap justify-center"
          style={{
            opacity: loaded ? 1 : 0,
            transition: `opacity 0.8s ease-out ${stagger(5)}`,
          }}
        >
          <span
            className="text-[10px] md:text-[11px] tracking-[0.06em]"
            style={{ fontFamily: 'var(--font-body)', color: 'rgba(248, 247, 245, 0.30)', fontWeight: 400 }}
          >
            CRM-CE 21438
          </span>
          <span className="w-[1px] h-3" style={{ backgroundColor: 'rgba(200, 169, 106, 0.25)' }} />
          <div className="flex items-center gap-[3px]">
            {[...Array(5)].map((_, i) => (
              <i key={i} className="ri-star-fill text-[8px] md:text-[9px]" style={{ color: 'rgba(200, 169, 106, 0.45)' }} />
            ))}
          </div>
          <span className="w-[1px] h-3" style={{ backgroundColor: 'rgba(200, 169, 106, 0.25)' }} />
          <span
            className="text-[10px] md:text-[11px] tracking-[0.06em]"
            style={{ fontFamily: 'var(--font-body)', color: 'rgba(248, 247, 245, 0.30)', fontWeight: 400 }}
          >
            Atendimento em Fortaleza
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20 cursor-pointer"
        style={{
          opacity: loaded ? 0.6 : 0,
          transition: `opacity 1s ease-out ${stagger(5) + 300}ms`,
        }}
        onClick={() => {
          document.getElementById('destaques')?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <span
          className="text-[10px] tracking-[0.2em]"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'rgba(248, 247, 245, 0.30)',
            fontWeight: 300,
          }}
        >
          Role para descobrir
        </span>
        <div className="animate-bounce" style={{ animationDuration: '2.5s' }}>
          <i className="ri-arrow-down-s-line text-lg" style={{ color: 'rgba(200, 169, 106, 0.45)' }} />
        </div>
      </div>

      {/* Organic wave transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 z-[5] pointer-events-none leading-[0]">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-[60px] md:h-[80px] lg:h-[100px] block"
          preserveAspectRatio="none"
        >
          <path
            d="M0 120L60 112C120 104 240 88 360 82C480 76 600 84 720 90C840 96 960 96 1080 90C1200 84 1320 74 1380 69L1440 64V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="#0E1412"
          />
        </svg>
      </div>
    </section>
  );
}